// A layout is written for a shop's viewport, so the preview renders it at a
// fixed logical width and scales the result down to whatever column it sits in.
// Sizing the iframe to the column instead crosses the layout's own media queries
// — the stock slider drops to 2 items under 768px — and previews something the
// shopper never sees on desktop.
export const DESIGN_WIDTHS = { email: 700, search: 1100 };
export const DEFAULT_DESIGN_WIDTH = 1200;

// A banner the SDK shows on phones only carries a media query that hides its
// modal at 481px and up, so previewing it at a desktop width shows an empty box.
// Under that breakpoint, at the width of a common phone.
export const MOBILE_DESIGN_WIDTH = 390;

// A pop-up, bar or flyout is `position: fixed`: it is sized by the viewport and
// contributes nothing to the document's own height, so there is nothing to
// measure. The frame stands in for a shop viewport instead, which is also what
// makes the overlay colour and a bar's top/bottom position visible at all.
export const SHOP_VIEWPORT_HEIGHT = 700;

// The same stand-in for a phone: 390x844 is the iPhone 14/15 logical viewport,
// which is also where `MOBILE_DESIGN_WIDTH` comes from. A banner's chrome is
// sized against the viewport, so previewing one on a phone means rendering it
// in a phone-shaped viewport, not drawing a bezel around a desktop one.
export const MOBILE_VIEWPORT_HEIGHT = 844;

// The visible window onto the preview. A taller layout is not cut off: the
// iframe keeps its full height and the wrapper scrolls.
export const MIN_BOX_HEIGHT = 200;
export const MAX_BOX_HEIGHT = 800;

// `scrollHeight` leaves out a trailing collapsed margin, which clips the last
// row of a layout by a pixel or two — and a clipped bottom border reads as a
// rendering bug. A few pixels of slack keeps the bottom edge intact.
export const CONTENT_SLACK = 8;

// Bounds what a single measurement may report. A layout sized against the
// viewport (`height: 100vh`, `height: 100%`) grows as the frame grows, which
// would measure taller, grow again, and never settle.
export const MAX_CONTENT_HEIGHT = 4000;

export const clampContentHeight = (measured) => Math.max(0, Math.min(Math.ceil(measured), MAX_CONTENT_HEIGHT));

// `availableWidth` is measured on the wrapper, which is not affected by the
// viewport's own chrome — its border and, when a tall layout scrolls, its
// scrollbar. Fitting to the full width would leave the preview a few pixels too
// wide and put a horizontal scrollbar under it, so hold that much back.
export const VIEWPORT_CHROME = 24;

// `viewport` is the device the author asked to see the block on. `mobileOnly`
// is the SDK's own answer about the block — a banner carrying a media query
// that hides it above 480px — and it still wins: asking to see such a block on
// a desktop can only produce an empty box, so the choice is honoured where it
// can be and overridden where it cannot.
export const getPreviewFit = ({ scope, mobileOnly, viewport, availableWidth, contentHeight }) => {
  const phone = mobileOnly || viewport === 'mobile';
  const designWidth = phone ? MOBILE_DESIGN_WIDTH : DESIGN_WIDTHS[scope] || DEFAULT_DESIGN_WIDTH;
  const usableWidth = Math.max(0, availableWidth - VIEWPORT_CHROME);

  // Never scale up: a layout in a column wider than the viewport it was written
  // for renders 1:1 and centres, rather than being blown up past its own
  // breakpoints. Before the first measurement (width 0) render 1:1 too — the
  // wrapper clips for the one frame it takes the resize observer to report.
  const scale = usableWidth > 0 ? Math.min(1, usableWidth / designWidth) : 1;

  const footprintHeight = Math.round(contentHeight * scale);

  return {
    designWidth,
    scale,
    // The scaled element keeps its unscaled layout box, so the footprint is what
    // actually reserves space in the column.
    footprintWidth: Math.round(designWidth * scale),
    footprintHeight,
    boxHeight: Math.min(Math.max(footprintHeight, MIN_BOX_HEIGHT), MAX_BOX_HEIGHT),
  };
};
